import React from 'react';
import { useTranslation } from 'react-i18next';

import { IconCheck, IconClose, IconExclamation, IconInfo } from '../Icon';
import Loader from '../Loader';
import {
  Action,
  ActionLink,
  ActionRouterLink,
  Body,
  CloseBtn,
  Content,
  Heading,
  Icon,
  Wrapper,
} from './wrapper';

interface AlertActionProps {
  onClick: () => void;
  title?: string;
  displayName: string;
  loading?: boolean;
  disabled?: boolean;
}

interface AlertLinkProps {
  href: string;
  title?: string;
  displayName: string;
  target?: string;
  rel?: string;
}

export type AlertToneType = 'success' | 'warning' | 'error' | 'info';

export interface AlertProps {
  tone?: AlertToneType;
  heading?: string;
  message?: string | React.ReactNode;
  children?: React.ReactNode;
  action?: AlertActionProps;
  link?: AlertLinkProps;
  routerLink?: AlertLinkProps;
  onClose?: () => void;
}

const getIcon = (tone: string) => {
  switch (tone) {
    case 'success':
      return <IconCheck />;
    case 'warning':
    case 'error':
      return <IconExclamation />;
    default:
      return <IconInfo />;
  }
};

const Alert = ({
  tone,
  heading,
  message,
  children,
  action,
  link,
  routerLink,
  onClose,
}: AlertProps) => {
  const { t } = useTranslation('common');

  return (
    <Wrapper $tone={tone}>
      <Icon aria-hidden="true">{getIcon(tone || '')}</Icon>
      <Content>
        {heading && <Heading>{heading}</Heading>}
        {message && <Body>{message}</Body>}
        {children}
        {action && (
          <Action
            onClick={action.onClick}
            disabled={action.disabled}
            title={action.title || action.displayName}
          >
            {action.displayName}
            {action.loading && <Loader inline />}
          </Action>
        )}
        {link && (
          <ActionLink
            href={link.href}
            title={link.title || link.displayName}
            target={link.target}
            rel={link.rel}
          >
            {link.displayName}
          </ActionLink>
        )}
        {routerLink && (
          <ActionRouterLink
            to={routerLink.href}
            title={routerLink.title || routerLink.displayName}
            target={routerLink.target}
            rel={routerLink.rel}
          >
            {routerLink.displayName}
          </ActionRouterLink>
        )}
      </Content>
      {onClose && (
        <CloseBtn onClick={onClose} title={t('close', { ns: 'common' }) || ''}>
          <IconClose />
        </CloseBtn>
      )}
    </Wrapper>
  );
};

export default Alert;
